import React, { useContext, useMemo, useState, useCallback } from 'react';
import "./styles.css";
import { Button, Spinner, useToast } from '@chakra-ui/react';
import { AutoComplete } from '../../Components/Atoms';
import { ReactTable, DatePicker } from '../../Components/Molecules';
import { VEHICLE_ACTIVITY_HEADERS } from '../../Library/tableHeaders';
import { VEH_FIL_CT } from '../../Library/constants';
import { StoreContext } from '../../Library/contexts';
import { jsonToCSVConvertor } from '../../Library/jsonUtils';
import { SearchIcon, VehicleActivityBlank } from '../../Assets/images/svgs';
import { getVehiclesWithFilter } from '../../APIs';

const VehicleActivity = () => {
  const { userVehicles } = useContext(StoreContext);
  const [filterPayload, setFilterPayload] = useState({ fromTs: "", toTs: "", cmpTyp: "", cmpVal: "", });
  const [lastSearchedPayload, setLastSearchedPayload] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [showSelectDateRange, setShowSelectDateRange] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const Toast = useToast();

  const isDisabled = useMemo(() => {
    return !filterPayload.fromTs || !filterPayload.toTs || !filterPayload.cmpTyp || !filterPayload.cmpVal;
  }, [filterPayload]);

  const handleDateRangeSave = useCallback((data) => {
    setFilterPayload((prev) => ({ ...prev, fromTs: data.fromTs, toTs: data.toTs }));
  }, []);

  const handleSearch = useCallback(() => {
    if (isDisabled) return;
    if (JSON.stringify(filterPayload) === JSON.stringify(lastSearchedPayload)) return;

    setIsLoading(true);

    getVehiclesWithFilter(filterPayload, (err, data = []) => {
      setIsLoading(false);
      if (err) {
        try {
          err = JSON.parse(err);
        } catch (error) {
          return console.error(error);
        }
        Toast({ isClosable: true, description: err.data, position: "bottom", variant: "subtle", duration:"3000"})
        console.error(err);
        return;
      }

      const userVehMap = (userVehicles || []).reduce((acc, veh) => {
        acc[veh.idx] = veh;
        return acc;
      }, {});

      setFilteredData(
        data.map((obj) => ({
          ...obj,
          dst: obj.dst ? (obj.dst / 1000).toFixed(2) : 0,
          reg: userVehMap[obj.vid]?.reg || "-",
          chNum: userVehMap[obj.vid]?.chNum || "-",
        }))
      );

      setLastSearchedPayload(filterPayload);
      setShowSelectDateRange(false);
    });
  }, [filterPayload, isDisabled, lastSearchedPayload, userVehicles]);

  const columns = useMemo(() => {
    return Object.values(VEHICLE_ACTIVITY_HEADERS).map((header) => ({
      Header: header.nm,
      accessor: header.key,
      disableSortBy: !header.sorting,
      Cell: ({ value }) => value || "-",
    }));
  }, []);

  const downloadData = useCallback(() => {
    if (!filteredData.length) return;

    const csvData = filteredData.map(({ reg, chNum, dst }, idx) => ({
      "Sr No": idx + 1,
      "Registration Number": reg,
      "Chassis Number": chNum,
      "Distance": dst,
    }));

    jsonToCSVConvertor(csvData, "", true, `VEHICLE_ACTIVITY_${Date.now()}`, false);
  }, [filteredData]);

  return (
    <div className="panel-main vehicle-activity">
      <header className="filter-cnt">
        <DatePicker showDefaultRanges={false} onSave={handleDateRangeSave} />
        <AutoComplete
          options={VEH_FIL_CT}
          isSearchable={false}
          placeholder="Select Condition"
          autoCompleteClassName="typ-select"
          onChange={(val) => setFilterPayload((prev) => ({ ...prev, cmpTyp: val }))}
        />
        <div className="val-input">
          <input
            type="number"
            placeholder="E.g: 500"
            value={filterPayload.cmpVal}
            onChange={(e) => setFilterPayload((prev) => ({ ...prev, cmpVal: e.target.value }))}
          />
          <span>Kms</span>
        </div>
        <Button disabled={isDisabled} leftIcon={<SearchIcon />} className={`srch-btn ${isDisabled ? "srch-btn-disabled" : ""}`} onClick={handleSearch} >
          Search
        </Button>
      </header>

      {isLoading ? (
        <div className="spinner-cnt">
          <Spinner className="spinner" size="lg" />
        </div>
      ) : showSelectDateRange ? (
        <div className="no-filteredData">
          <VehicleActivityBlank />
          <span>Select a date range and enter a distance to view your vehicle activity</span>
        </div>
      ) : (
        <ReactTable
          columns={columns}
          data={filteredData}
          showSearch
          heading="Vehicles"
          pageSize={10}
          showDownload
          downloadHandler={downloadData}
        />
      )}
    </div>
  );
};

export default VehicleActivity;